import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { formatDateShort } from "@/shared/lib/format-date";
import { formatMoney, moneyFromCentsString } from "./money";
import type { VenueBalanceGroup } from "./group-balances-by-venue";
import type { PendingBalanceItem } from "./types";

interface VenueBalanceCardProps {
  readonly group: VenueBalanceGroup;
  /**
   * When provided (WANDERFIT_ADMIN only), renders the per-venue Pay action.
   * Absent for FINANCE / read-only viewers.
   */
  readonly onPay?: (group: VenueBalanceGroup) => void;
  /**
   * When provided (WANDERFIT_ADMIN only), renders the per-currency Liquidate
   * (write-off) action on each balance row. Absent for read-only viewers.
   */
  readonly onLiquidate?: (item: PendingBalanceItem) => void;
}

function ConnectBadge({ item }: { readonly item: PendingBalanceItem }) {
  const { t } = useTranslation();
  const payable = item.hasConnect && item.payoutsEnabled;
  return (
    <span
      data-testid={`connect-status-${item.venueId}-${item.currency}`}
      className={
        payable
          ? "inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600"
          : "inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600"
      }
    >
      {payable ? t("finance.report.status.payable") : t("finance.report.status.blocked")}
    </span>
  );
}

export function VenueBalanceCard({ group, onPay, onLiquidate }: VenueBalanceCardProps) {
  const { t, i18n } = useTranslation();
  const owner = group.ownerDisplayName;

  return (
    <article
      data-testid={`venue-balance-card-${group.venueId}`}
      className="space-y-4 rounded-lg border border-border bg-card p-4"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h2 className="font-display text-lg">{group.venueName ?? group.venueId}</h2>
          <p className="font-sans text-sm text-muted-foreground">
            {owner ?? t("finance.report.unknownOwner")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/finance/accruals?venueId=${encodeURIComponent(group.venueId)}`}
            data-testid={`view-accruals-${group.venueId}`}
            className="font-sans text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {t("finance.report.actions.viewAccruals")}
          </Link>
          {onPay && (
            <Button
              data-testid={`pay-venue-button-${group.venueId}`}
              size="sm"
              onClick={() => onPay(group)}
              disabled={!group.anyPayable}
              title={group.anyPayable ? undefined : t("finance.report.actions.payBlocked")}
            >
              {t("finance.report.actions.pay")}
            </Button>
          )}
        </div>
      </header>

      <ul className="space-y-2">
        {group.balances.map((item) => (
          <li
            key={item.currency}
            data-testid={`payout-row-${item.venueId}-${item.currency}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-background/40 px-3 py-2"
          >
            <div className="space-y-0.5">
              <p className="font-mono text-sm font-medium text-foreground">
                {formatMoney(moneyFromCentsString(item.totalPendingCents, item.currency))}{" "}
                <span className="uppercase text-muted-foreground">{item.currency}</span>
              </p>
              <p className="font-sans text-xs text-muted-foreground">
                {t("finance.report.oldestAccrual", {
                  date: formatDateShort(item.oldestAccrualAt, i18n.language),
                  count: item.accrualCount,
                })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ConnectBadge item={item} />
              {onLiquidate && (
                <Button
                  data-testid={`liquidate-venue-button-${item.venueId}-${item.currency}`}
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onLiquidate(item)}
                >
                  {t("finance.report.actions.liquidate")}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </article>
  );
}
